import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchNotifications, createNotification, updateNotification, deleteNotification } from '../../services/notificationService';

interface Notification {
  id: number;
  title: string;
  content: string;
  date: string;
  time: string;
  repeat: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface NotificationsState {
  items: Notification[];
  total: number;
  currentPage: number;
  totalPages: number;
  loading: boolean;
  error: string | null;
}

const initialState: NotificationsState = {
  items: [],
  total: 0,
  currentPage: 1,
  totalPages: 1,
  loading: false,
  error: null,
};

export const fetchNotificationsAsync = createAsyncThunk(
  'notifications/fetchNotifications',
  async ({ page, limit }: { page: number; limit: number }) => {
    const response = await fetchNotifications(page, limit);
    return {
      data: response.data,
      total: response.total,
      page: response.page,
      totalPages: response.totalPages,
    };
  }
);

export const createNotificationAsync = createAsyncThunk(
  'notifications/createNotification',
  async (data: Partial<Notification>) => {
    const response = await createNotification(data);
    return response;
  }
);

export const updateNotificationAsync = createAsyncThunk(
  'notifications/updateNotification',
  async ({ id, data }: { id: number; data: Partial<Notification> }, { rejectWithValue }) => {
    try {
      const response = await updateNotification(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update notification');
    }
  }
);

export const deleteNotificationAsync = createAsyncThunk(
  'notifications/deleteNotification',
  async (id: number, { rejectWithValue }) => {
    try {
      await deleteNotification(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete notification');
    }
  }
);

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotificationsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.total = action.payload.total;
        state.currentPage = action.payload.page;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchNotificationsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch notifications';
      })
      .addCase(createNotificationAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createNotificationAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
      })
      .addCase(createNotificationAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create notification';
      })
      .addCase(updateNotificationAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateNotificationAsync.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateNotificationAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update notification';
      })
      .addCase(deleteNotificationAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteNotificationAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(item => item.id !== action.payload);
      })
      .addCase(deleteNotificationAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete notification';
      });
  },
});

export default notificationsSlice.reducer;